import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe, logout as apiLogout, fetchFavoriteIds } from "../lib/api";
import LoginModal from "../components/LoginModal";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favIds, setFavIds] = useState(new Set());
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginTab, setLoginTab] = useState("signin");

  const refreshFavs = useCallback(async () => {
    try {
      const ids = await fetchFavoriteIds();
      setFavIds(new Set(ids));
    } catch {
      setFavIds(new Set());
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      const ids = await fetchFavoriteIds().catch(() => []);
      setFavIds(new Set(ids));
    } catch {
      setUser(null);
      setFavIds(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me if returning from OAuth (AuthCallback handles it)
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const signOut = async () => {
    await apiLogout().catch(() => {});
    setUser(null);
    setFavIds(new Set());
  };

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACK OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  const openLogin = (tab = "signin") => {
    setLoginTab(tab);
    setLoginOpen(true);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, signOut, startGoogleLogin, openLogin, favIds, refreshFavs }}
    >
      {children}
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} defaultTab={loginTab} />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
